import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useDispatch } from 'react-redux'
import PermissionChecker from '~/Classes/permissions'
import useTierAllowed from '~/Common-Objects/hooks/useTierAllowed'
import Lab from '~/Common-Objects/Labs/lab'
import CheckboxTile from '~/Common-Objects/UI/Forms/CheckboxTile'
import Modal from '~/Common-Objects/Modal/index'
import Paper from '~/Common-Objects/UI/Paper/Paper'
import UpsellModal from '~/Common-Objects/UpsellModal'
import { EnableFeature as LabsEnable } from '~/Common-Objects/Labs/enableFeature'
import { alterFullpage, alterSiteDrawer } from '~/data/actions/siteActions'
import axios from '~/data/http/axios'

export default function DeliveryOptions({ options, setOptions }) {
    const account = useSelector((state) => state.accountMaster)
    const site = useSelector((state) => state.siteMaster)
    const permissionStore = useSelector((state) => state.permission)
    const dispatch = useDispatch()

    const [upsellClicked, setUpsellClicked] = useState('')
    const [labs, setLabs] = useState(null)
    const [enablingLab, setEnablingLab] = useState(false)

    const threeWaveTierCheck = useTierAllowed('threeWaveSending')
    const timeBasedTierCheck = useTierAllowed('timeBasedSending')
    const canEnableLabs = PermissionChecker('company', permissionStore.permissions, 'write')

    const handleChange = (e) => {
        const { value } = e.target
        if (value === 'threeWaveSending' && site.siteId === 1) {
            // if (!account.accountMaster.beta?.optimisedSending) {
            //     setUpsellClicked('threeWaveSending')
            //     return showLabs()
            // }
            if (!threeWaveTierCheck.tierAllowed) {
                setUpsellClicked('threeWaveSending')
                upsell('threeWaveSending', threeWaveTierCheck.requiredTier?.name || 'Starter')
                return
            }
        }
        if (value === 'timeBasedSending' && site.siteId === 1) {
            // if (!account.accountMaster.beta?.optimisedSending) {
            //     setUpsellClicked('timeBasedSending')
            //     return showLabs()
            // }
            if (!timeBasedTierCheck.tierAllowed) {
                setUpsellClicked('timeBasedSending')
                upsell('timeBasedSending', timeBasedTierCheck.requiredTier?.name || 'Growth')
                return
            }
        }

        setUpsellClicked('')

        setOptions({
            ...options,
            threeWaveSending: value === 'threeWaveSending',
            timeBasedSending: value === 'timeBasedSending'
        })
    }

    const showLabs = () => {
        const lab = labs.find((l) => l.name === 'optimisedSending')
        const modal = (
            <Modal
                width={800}
                height={240}
                top={false}
                blurBg={true}
                paperClass='with_header mar-b0'
                closeButton={true}
                closeButtonClass='topRight'
                onCloseButton={() => {
                    dispatch(alterFullpage(null))
                    setEnablingLab(false)
                    setUpsellClicked('')
                }}
            >
                <Lab
                    lab={lab}
                    enable={() => enableLab(lab)}
                    canEnableLabs={canEnableLabs}
                    paperClass='mar-b0 no-border'
                />
            </Modal>
        )
        dispatch(alterFullpage(modal))
    }

    const enableLab = (lab) => {
        console.log(lab)
        const modal = (
            <Modal top={false} blurBg={true} paperClass='with_header mar-b0'>
                <LabsEnable
                    labId={lab.id}
                    inline={true}
                    onClose={() => {
                        dispatch(alterFullpage(null))
                        setEnablingLab(false)
                        getLabs()
                    }}
                />
            </Modal>
        )
        dispatch(alterFullpage(modal))
    }

    const upsell = (feature, tier) => {
        const modal = (
            <UpsellModal
                feature={feature}
                requiredTier={tier}
                onUpgrade={() => {
                    dispatch(alterSiteDrawer(false, true, 'right', null))
                    getTier()
                }}
            />
        )
        dispatch(alterFullpage(modal))
    }

    const getLabs = async () => {
        try {
            const res = await axios.get('/accountMaster/labs')
            setLabs(res.data)
        } catch (e) {
            setLabs([])
        }
    }

    useEffect(() => {
        if (upsellClicked) {
            if (upsellClicked === 'threeWaveSending' && threeWaveTierCheck.tierAllowed) {
                handleChange({ target: { value: 'threeWaveSending' } })
            } else if (upsellClicked === 'timeBasedSending' && timeBasedTierCheck.tierAllowed) {
                handleChange({ target: { value: 'timeBasedSending' } })
            }
            setUpsellClicked('')
        }
    }, [threeWaveTierCheck.tierAllowed, timeBasedTierCheck.tierAllowed])

    useEffect(() => {
        getLabs()
        if (upsellClicked && account?.accountMaster?.beta) {
            handleChange({ target: { value: upsellClicked } })
        }
    }, [account?.accountMaster?.beta, upsellClicked])

    if (site.siteId !== 1 && site.siteId !== 234) return null
    // if (!account.accountMaster.beta?.optimisedSending && !labs) return null
    // if (site.siteId === 1 && !labs?.some((l) => l.name === 'optimisedSending')) return null
    // if (site.siteId === 1 && (!threeWaveTierCheck.requiredTier || !timeBasedTierCheck.requiredTier)) return null

    return (
        <Paper>
            <div className='quickFlex' style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <h4 style={{ marginBottom: '4px' }}>Delivery Options</h4>
                    <p className='mar-b25' style={{ color: 'var(--neutral-700)', fontSize: '13px' }}>
                        Choose how your campaign is delivered to balance speed, deliverability, or engagement.{' '}
                        <a
                            href='https://transpond.io/support/deliverability-and-compliance/smart-sending'
                            target='_blank'
                            style={{ textDecoration: 'none' }}
                        >
                            Learn more
                        </a>
                    </p>
                </div>
            </div>

            <div className='row'>
                <div className='col-xs-12 col-sm-4'>
                    <CheckboxTile
                        name='deliveryOptions'
                        value='fast'
                        checked={!options.threeWaveSending && !options.timeBasedSending}
                        onChange={handleChange}
                        data-ph={'delivery-options-fast'}
                        outerStyle={{ height: '100%' }}
                        header='Fast'
                        subheader='All emails sent at once for the quickest possible delivery.'
                        icon='rabbit'
                    />
                </div>
                <div className='col-xs-12 col-sm-4'>
                    <CheckboxTile
                        name='deliveryOptions'
                        value='threeWaveSending'
                        checked={!!options.threeWaveSending}
                        onChange={handleChange}
                        data-ph={
                            threeWaveTierCheck.tierAllowed || site.siteId !== 1
                                ? 'delivery-options-threewave'
                                : 'delivery-options-threewave-upsell'
                        }
                        requiredTier={
                            !threeWaveTierCheck.tierAllowed &&
                            site.siteId === 1 &&
                            threeWaveTierCheck.requiredTier?.name
                        }
                        // beta={true}
                        outerStyle={{ height: '100%' }}
                        header='Optimised Delivery'
                        subheader='Sent in waves to improve deliverability and avoid spam filters.'
                        icon='inbox'
                    />
                </div>
                <div className='col-xs-12 col-sm-4'>
                    <CheckboxTile
                        name='deliveryOptions'
                        value='timeBasedSending'
                        checked={!!options.timeBasedSending}
                        onChange={handleChange}
                        data-ph={
                            timeBasedTierCheck.tierAllowed || site.siteId !== 1
                                ? 'delivery-options-timebased'
                                : 'delivery-options-timebased-upsell'
                        }
                        requiredTier={
                            !timeBasedTierCheck.tierAllowed &&
                            site.siteId === 1 &&
                            timeBasedTierCheck.requiredTier?.name
                        }
                        // beta={true}
                        outerStyle={{ height: '100%' }}
                        header='Best Engagement'
                        subheader='Emails sent at the best time for each contact based on their past behaviour.'
                        icon='sparkle3'
                    />
                </div>
            </div>
            {options.timeBasedSending && (
                <div
                    style={{
                        marginTop: 24,
                        fontWeight: 400,
                        fontSize: 12,
                        lineHeight: '16px',
                        letterSpacing: '-0.1px'
                    }}
                >
                    We will decide on the best time to send emails to your contacts. Your emails will still be sent
                    within 24 hours.{' '}
                    <a href='https://transpond.io/support/deliverability-and-compliance/smart-sending' target='_blank'>
                        Learn more
                    </a>
                </div>
            )}
        </Paper>
    )
}
